import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, X, PlayCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FaqSection } from '@/components/FaqSection';

const HELP_FAQ = [
  {
    id: 'more-players',
    question: 'How do I get more players to join?',
    answer: 'Open your team and tap the join code to copy it, then share it with parents. Players enter the code on their Profile page under "Join a Team" and appear on your roster instantly. You can also search by username in "Add Players" and add them directly.',
  },
  {
    id: 'assign-specific',
    question: 'How do I assign content to specific players?',
    answer: 'In "My Content", add a video, article, or note and choose which roster players it goes to. Leave the selection empty to share it with the whole team. Assigned players get a notification on their home screen right away.',
  },
  {
    id: 'upgrade',
    question: 'How do I upgrade to Coach Pro?',
    answer: 'Coach Pro is coming soon — Coaches Hub is free during the beta. Tap "See Plans" on the Coach Pro banner to get notified when it launches.',
  },
  {
    id: 'analytics',
    question: 'How do I read my analytics?',
    answer: 'The stats grid shows active players, average Game IQ level, total team XP, and assignment completion. The health row groups players as Active (last 7 days), At Risk (7-14 days), or Inactive (14+ days) — tap one to filter your roster.',
  },
];

interface CoachHelpButtonProps {
  onReplayTour: () => void;
}

export default function CoachHelpButton({ onReplayTour }: CoachHelpButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Coach help and tour"
        className="fixed right-4 z-[900] w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-elevated ring-1 ring-primary/50 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
        style={{ bottom: `calc(1rem + env(safe-area-inset-bottom, 0px))` }}
      >
        <HelpCircle className="w-6 h-6" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[1100] bg-background/80 backdrop-blur-sm flex items-end sm:items-center justify-center"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            role="dialog" aria-modal="true" aria-label="Coach help"
          >
            <motion.div
              onClick={e => e.stopPropagation()}
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 280, damping: 26 }}
              className="w-full sm:max-w-md max-h-[85vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl glass-card border-2 border-[#2563eb] shadow-elevated p-5"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold text-lg text-foreground">Coach Help</h3>
                <button onClick={() => setOpen(false)} aria-label="Close help" className="text-muted-foreground hover:text-foreground">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <Button
                className="w-full font-display font-bold mb-4"
                onClick={() => { setOpen(false); onReplayTour(); }}
              >
                <PlayCircle className="w-4 h-4 mr-2" /> Replay the guided tour
              </Button>

              <FaqSection title="Quick answers" items={HELP_FAQ} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
